import { createFileRoute } from '@tanstack/react-router';
import { ADMIN_SEARCH_MAX } from '@leanin/shared/admin-search';
import { AdminMembersView } from '@/components/admin/AdminMembersView';
import type { ListAdminMembersInput } from '@/lib/queries/admin';

type Status = 'active' | 'pending' | 'restricted' | 'deactivated' | 'banned';
type PlatformRoleFilter = 'admin' | 'moderator' | 'none';
type CommunityRoleFilter = 'circle_leader' | 'network_leader';

interface MembersSearch {
  readonly q?: string;
  readonly status?: Status;
  readonly platformRole?: PlatformRoleFilter;
  readonly communityRole?: CommunityRoleFilter;
  readonly cursor?: string;
}

const ALLOWED_STATUS: ReadonlyArray<Status> = [
  'active',
  'pending',
  'restricted',
  'deactivated',
  'banned',
];

const ALLOWED_PLATFORM_ROLE: ReadonlyArray<PlatformRoleFilter> = [
  'admin',
  'moderator',
  'none',
];

const ALLOWED_COMMUNITY_ROLE: ReadonlyArray<CommunityRoleFilter> = [
  'circle_leader',
  'network_leader',
];

export const Route = createFileRoute('/_authed/admin/members/')({
  validateSearch: (raw: Record<string, unknown>): MembersSearch => {
    // An oversize `q` would 422 the whole list, so it reads as absent.
    const q =
      typeof raw.q === 'string' &&
      raw.q.length > 0 &&
      raw.q.length <= ADMIN_SEARCH_MAX
        ? raw.q
        : undefined;
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_STATUS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as Status)
        : undefined;
    const platformRole =
      typeof raw.platformRole === 'string' &&
      (ALLOWED_PLATFORM_ROLE as ReadonlyArray<string>).includes(raw.platformRole)
        ? (raw.platformRole as PlatformRoleFilter)
        : undefined;
    const communityRole =
      typeof raw.communityRole === 'string' &&
      (ALLOWED_COMMUNITY_ROLE as ReadonlyArray<string>).includes(raw.communityRole)
        ? (raw.communityRole as CommunityRoleFilter)
        : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    // Rejected keys are returned as explicit `undefined`: the router merges
    // this result over the raw URL search, so an omitted key would let the
    // raw value through to `useSearch()`.
    return { q, status, platformRole, communityRole, cursor };
  },
  component: AdminMembersPage,
});

function AdminMembersPage() {
  const search = Route.useSearch();
  const listInput: ListAdminMembersInput = {
    pageSize: 50,
    ...(search.q ? { q: search.q } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.platformRole ? { platformRole: search.platformRole } : {}),
    ...(search.communityRole ? { communityRole: search.communityRole } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return <AdminMembersView listInput={listInput} cursor={search.cursor} />;
}
